import { ImagePlus, Upload } from "lucide-react";
import { useRef } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { useToast } from "../../components/ui/Toast";
import { cn } from "../../lib/cn";
import { formatDate } from "../../lib/format";
import type { MediaAsset } from "../../lib/types";
import { assetUrl, useMediaLibrary, useUploadMedia } from "./useMedia";

const formatBytes = (bytes: number): string =>
	bytes < 1024 * 1024 ? `${Math.round(bytes / 1024)} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

interface MediaPickerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelect: (asset: MediaAsset) => void;
}

export const MediaPicker = ({ open, onOpenChange, onSelect }: MediaPickerProps) => {
	const library = useMediaLibrary();
	const upload = useUploadMedia();
	const toast = useToast();
	const fileInput = useRef<HTMLInputElement>(null);

	const handleFile = (file: File | undefined) => {
		if (!file) return;
		upload.mutate(file, {
			onSuccess: (asset) => {
				toast.push("Image uploaded");
				onSelect(asset);
			},
			onError: (error) => toast.push(error.message, "error"),
		});
	};

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title="Choose an image"
			description="Uploads are converted to WebP in your browser and stored in your library."
		>
			<div className="space-y-4">
				<input
					ref={fileInput}
					type="file"
					accept="image/png,image/jpeg,image/webp,image/gif"
					className="hidden"
					onChange={(event) => {
						handleFile(event.target.files?.[0]);
						event.target.value = "";
					}}
				/>

				<Button
					variant="outline"
					className="w-full"
					disabled={upload.isPending}
					onClick={() => fileInput.current?.click()}
				>
					<Upload className={cn("h-4 w-4", upload.isPending && "animate-pulse")} strokeWidth={2} />
					{upload.isPending ? "Converting and uploading…" : "Upload new image"}
				</Button>

				{library.isPending ? (
					<Skeleton className="h-48 w-full" />
				) : library.data && library.data.items.length === 0 ? (
					<EmptyState
						title="Library is empty"
						description="Uploaded images are reusable across every campaign."
					/>
				) : (
					<div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto">
						{library.data?.items.map((asset) => (
							<button
								key={asset.id}
								type="button"
								onClick={() => onSelect(asset)}
								className="group overflow-hidden rounded-md border border-line text-left transition-colors hover:border-line-strong"
							>
								<img
									src={assetUrl(asset.id)}
									alt={asset.filename ?? ""}
									loading="lazy"
									className="h-20 w-full bg-raised object-cover"
								/>
								<span className="block truncate px-1.5 py-1 text-[11px] text-ink-faint">
									{formatBytes(asset.bytes)} · {formatDate(asset.createdAt).split(",")[0]}
								</span>
							</button>
						))}
					</div>
				)}
			</div>
		</Dialog>
	);
};

export const ImageSlot = ({
	assetId,
	label,
	onPick,
	onClear,
}: {
	assetId: string | null;
	label: string;
	onPick: () => void;
	onClear?: () => void;
}) => (
	<div className="space-y-1.5">
		<span className="text-xs text-ink-muted">{label}</span>
		{assetId ? (
			<div className="space-y-1.5">
				<button
					type="button"
					onClick={onPick}
					className="block w-full overflow-hidden rounded-md border border-line transition-colors hover:border-line-strong"
				>
					<img src={assetUrl(assetId)} alt="" className="h-28 w-full bg-raised object-contain" />
				</button>
				<div className="flex gap-2">
					<Button variant="ghost" size="sm" onClick={onPick}>
						Replace
					</Button>
					{onClear ? (
						<Button variant="ghost" size="sm" onClick={onClear}>
							Remove
						</Button>
					) : null}
				</div>
			</div>
		) : (
			<button
				type="button"
				onClick={onPick}
				className="flex h-28 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-line text-xs text-ink-muted transition-colors hover:border-line-strong hover:text-ink"
			>
				<ImagePlus className="h-5 w-5" strokeWidth={1.5} />
				Choose image
			</button>
		)}
	</div>
);
